import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import api from '../api/api';

const CATEGORIES_MOBILE = {
  vehicle: 'Vehículo',
  wheel_loader: 'Pala Cargadora',
  backhoe_loader: 'Retroexcavadora',
  compactor: 'Compactador',
  telescopic_handler: 'Manipulador Telescópico',
  forklift: 'Carretilla Elevadora',
  aerial_platform: 'Plataforma Elevadora',
  sweeper: 'Barredora',
  shredder: 'Trituradora',
};

const CATEGORIES_FIXED = {
  conveyor: 'Cinta Transportadora',
  fixed_plant: 'Equipo de Planta',
};

const SECTIONS_FIXED = ['AFINO', 'PLANTA DE TRATAMIENTO'];

const STATUSES = { operative: 'Operativo', alert: 'Alerta', breakdown: 'Avería', inactive: 'Inactivo' };

export default function EquipmentModal({ type, equipment, onClose, onSaved }) {
  const isEdit = !!equipment;
  const [form, setForm] = useState({
    code: equipment?.code || '',
    name: equipment?.name || '',
    type,
    section: equipment?.section || (type === 'fixed' ? 'AFINO' : ''),
    category: equipment?.category || (type === 'mobile' ? 'vehicle' : 'fixed_plant'),
    status: equipment?.status || 'operative',
    description: equipment?.description || '',
    year: equipment?.year || new Date().getFullYear(),
    serial_number: equipment?.serial_number || '',
    notes: equipment?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const categories = type === 'mobile' ? CATEGORIES_MOBILE : CATEGORIES_FIXED;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        const { type: _t, ...payload } = form;
        await api.put(`/equipment/${equipment.id}`, payload);
      } else {
        await api.post('/equipment', form);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="equipment-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>
            {isEdit ? 'Editar Equipo' : `Nuevo Equipo ${type === 'mobile' ? 'Móvil' : 'Fijo'}`}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded" data-testid="close-equipment-modal">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="equipment-modal-error">{error}</div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Código / Matrícula</label>
              <input
                type="text"
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value })}
                required
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none"
                placeholder="Ej: CAT-950G"
                data-testid="eq-code"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Categoría</label>
              <select
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white"
                data-testid="eq-category"
              >
                {Object.entries(categories).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nombre del equipo</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              required
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none"
              placeholder="Nombre completo del equipo"
              data-testid="eq-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {type === 'fixed' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Sección</label>
                <select
                  value={form.section}
                  onChange={e => setForm({ ...form, section: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white"
                  data-testid="eq-section"
                >
                  {SECTIONS_FIXED.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white"
                data-testid="eq-status"
              >
                {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none resize-none"
              data-testid="eq-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Año</label>
              <input
                type="number"
                value={form.year}
                onChange={e => setForm({ ...form, year: parseInt(e.target.value) })}
                min="1990" max="2030"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none"
                data-testid="eq-year"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Nº Serie</label>
              <input
                type="text"
                value={form.serial_number}
                onChange={e => setForm({ ...form, serial_number: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none"
                data-testid="eq-serial"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Observaciones</label>
            <textarea
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none resize-none"
              data-testid="eq-notes"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="cancel-equipment-btn">Cancelar</button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d]"
              data-testid="save-equipment-btn"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {saving ? 'Guardando...' : isEdit ? 'Guardar Cambios' : 'Crear Equipo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
